<template>
    <div class="form-group row">
      <label class="col-sm-2 col-form-label">
        {{ c.label }}<small v-if="c.required" class="text-danger">*</small>
      </label>
      <div class="col-sm-10 my-auto" v-if="!multiple">
        <!-- On the kiosk the "is this e-mail taken" check runs while the guest
             is still typing, so it reports itself inside the field (see
             `is_kiosk` below) instead of raising the full-screen overlay. -->
        <div class="email-field" :class="{ 'email-field-kiosk': is_kiosk }">
          <input
            v-if="!confirm"
            v-model="inputEmail"
            v-half-width
            type="email"
            v-debounce:500ms="checkEmail"
          v-half-width
            :required="c.required ? true : false"
            class="form-control w-100"
            :placeholder="c.placeholder"
          />
          <span v-if="is_kiosk && !confirm" class="email-field-status" aria-live="polite">
            <i v-if="checking_email" class="fas fa-spinner fa-spin email-field-spinner" aria-hidden="true"></i>
            <i v-else-if="email_invalid" class="fas fa-exclamation-circle email-field-warn" aria-hidden="true"></i>
            <i v-else-if="email_checked && !email_exists" class="fas fa-check email-field-check" aria-hidden="true"></i>
          </span>
        </div>
        <p v-if="confirm" class="mb-0">{{ inputEmail }}</p>
        <small style="font-size: 15px" v-if="is_kiosk && email_invalid" class="form-text text-danger">
          {{ $t("checkout_by_phone.email_invalid") }}
        </small>
        <small style="font-size: 15px" v-if="email_exists" class="form-text text-danger">
          {{ $t("visit-information-form.email_exist_by_facility") }}
        </small>
      </div>

      <!-- Multiple Room -->
      <div class="col-sm-10 my-auto" v-else>
        <div class="email-field" :class="{ 'email-field-kiosk': is_kiosk }">
          <input
            v-if="!confirm"
            v-model="multiple_data[bd_index].email"
          v-half-width
            type="email"
            v-debounce:500ms="checkEmail"
            :required="multiple_data[bd_index].filled_in_by_other ? false : c.required ? true : false"
            class="form-control w-100"
            :placeholder="c.placeholder"
          />
          <span v-if="is_kiosk && !confirm" class="email-field-status" aria-live="polite">
            <i v-if="checking_email" class="fas fa-spinner fa-spin email-field-spinner" aria-hidden="true"></i>
            <i v-else-if="email_invalid" class="fas fa-exclamation-circle email-field-warn" aria-hidden="true"></i>
            <i v-else-if="email_checked && !email_exists" class="fas fa-check email-field-check" aria-hidden="true"></i>
          </span>
        </div>
        <p v-if="confirm" class="mb-0">{{ multiple_data[bd_index].email }}</p>
        <small style="font-size: 15px" v-if="is_kiosk && email_invalid" class="form-text text-danger">
          {{ $t("checkout_by_phone.email_invalid") }}
        </small>
        <small style="font-size: 15px" v-if="email_exists" class="form-text text-danger">
          {{ $t("visit-information-form.email_exist_by_facility") }}
        </small>
      </div>
    </div>
  </template>

  <script>
  import { mapState, mapActions } from 'vuex';
  import { isKioskRoute } from '@/modules/checkin-facility/pages/checkin/kiosk/kiosk_config';

  // The pattern the kiosk hub screens already validate addresses with, so a
  // guest is judged by the same rule wherever the kiosk asks for an e-mail
  // (checkin_kiosk_hub_component.vue, checkout_kiosk_hub_component.vue).
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Once the in-field spinner is up, keep it up this long. The check often
  // answers in under 100ms, and a spinner that lives that briefly reads as a
  // flicker in the corner of the field rather than as an answer.
  const MIN_SPINNER_MS = 400;

  export default {
    props: {
      c: null,
      confirm: false,
      visitor: {
        type: Object,
        default: {}
      },
      user: {
        type: Object,
        default: {}
      },
      // Multiple room
      multiple: false,
      multiple_data: {
        type: Array,
        default: () => []
      },
      bd_index: null,
      // End of multiple room

      // checkin form type
      checkin_form_type: ""
    },
    data() {
      return {
        email_exists: false,
        inputEmail: '', // Initialize inputEmail with an empty string
        // Kiosk only: the field draws its own progress, so these track the
        // check the guest is waiting on rather than the page as a whole.
        checking_email: false,
        email_checked: false,
        // Set when what the guest typed cannot be an address at all — a
        // separate answer from `email_exists`, which is the server's.
        email_invalid: false,
        // Responses can land out of order once the guest keeps typing; only
        // the newest check is allowed to write the result.
        email_check_seq: 0,
        spinner_shown_at: 0,
        spinner_timer: null,
      };
    },
    watch: {
      'user.email': {
        handler(newVal) {
              this.loadEmail(newVal)
        },
      },
      inputEmail: {
        handler(newVal) {
          const maskedEmail = this.removeDummyEmail(newVal)
          this.saveEmail(maskedEmail);
        }
      },
      current_email: {
        handler(newVal) {
          // The answer on screen belongs to the previous value; drop it the
          // moment the guest edits, so a stale tick never sits beside new text.
          this.email_checked = false;
          // The format complaint, on the other hand, clears the instant the
          // address becomes well-formed — it is there to stop the guest, not
          // to sit under the field while they are busy fixing it.
          if (this.email_invalid && EMAIL_PATTERN.test(newVal)) this.email_invalid = false;
        }
      }
    },
    computed: {
      ...mapState('memorizedInputs', ['memorizedEmail']),
      is_kiosk() {
        return isKioskRoute(this.$route);
      },
      // The address this instance owns — its own field in a multiple-room
      // form, the shared one otherwise.
      current_email() {
        if (!this.multiple) return this.inputEmail;
        return this.multiple_data[this.bd_index]?.email ?? '';
      },
    },
    async mounted() {

      this.loadEmail(this.inputEmail);

      this.$root.$on('email_exists', (status) => {
        // Check mail form JS validateEmail method
        this.email_exists = status;
      });
    },
    beforeDestroy() {
      if (this.spinner_timer) clearTimeout(this.spinner_timer);
    },
    methods: {
      ...mapActions('memorizedInputs', ['saveEmail']),

      async loadEmail(newVal) {
        const memorizedEmail = await this.memorizedEmail;
        let newEmail;

        if(this.checkin_form_type !== 'others') {
          newEmail = memorizedEmail ? memorizedEmail : newVal;
        } else {
          newEmail = memorizedEmail ? memorizedEmail : '';
        }

        this.inputEmail = newEmail;
      },
      removeDummyEmail(val) {
        const dummyEmailPattern = /(.+@dummy\.com.*)|(.+@dummy\.co\.jp)|(dummy@nta.*)/i;

            if (dummyEmailPattern.test(val)) {
              this.saveEmail('')
              this.inputEmail = ''
            }
             return val
      },
      async checkEmail() {
        const email = this.current_email;

        if (email == "") {
          this.email_invalid = false;
          return;
        }

        // Kiosk only: an address that cannot be an address is answered in the
        // field and never sent — /user/exists has nothing useful to say about
        // "tes", and the guest would otherwise get a green tick for it.
        if (this.is_kiosk && !EMAIL_PATTERN.test(email)) {
          this.email_invalid = true;
          this.email_checked = false;
          return;
        }

        this.email_invalid = false;

        await this.validateEmail();
      },
      // Raise / lower the in-field spinner. Kiosk only — everywhere else the
      // site-wide overlay is still the thing that reports this request.
      startFieldSpinner() {
        if (!this.is_kiosk) return;
        if (this.spinner_timer) {
          clearTimeout(this.spinner_timer);
          this.spinner_timer = null;
        }
        this.checking_email = true;
        this.spinner_shown_at = Date.now();
      },
      stopFieldSpinner() {
        if (!this.checking_email) return;
        const remaining = MIN_SPINNER_MS - (Date.now() - this.spinner_shown_at);

        if (remaining <= 0) {
          this.checking_email = false;
          return;
        }

        this.spinner_timer = setTimeout(() => {
          this.spinner_timer = null;
          this.checking_email = false;
        }, remaining);
      },
      async validateEmail() {
        const data = {
          email: this.inputEmail,
          id: this.visitor.visitor_id,
          type: "visitor",
          module: "checkin",
        };
        const seq = ++this.email_check_seq;

        this.startFieldSpinner();

        // `quietKioskLoader` keeps this request out of the kiosk overlay's
        // pending count (checkin-lionheart/module/client.js): the guest is
        // mid-sentence in the field, and blanking the tablet under them is
        // both alarming and enough to close the on-screen keyboard.
        const email = await this.$lionheart
          .post("/user/exists", data, this.is_kiosk ? { quietKioskLoader: true } : {})
          .then((r) => this.catcher(r.data, "data.exist", (r) => r.data))
          .catch((e) => this.catcher(e));

        // A newer keystroke already started its own check; this answer is
        // about text the guest has moved on from.
        if (seq !== this.email_check_seq) return;

        this.stopFieldSpinner();

        if (email.error.has_error) return this.errorDisplay(email);
        this.email_exists = email.exist;
        this.email_checked = true;
        // Update grand parent
        this.$root.$emit('update_email_exist', this.email_exists);
      },
      errorDisplay(d) {
        // this.error_msg = d.error.msg;
        setTimeout(
          () => window.scrollTo(0, $("#error_msg").position().top - 10),
          300
        );
        return;
      },
    }
  };
  </script>

<style scoped>
.email-field {
    position: relative;
}

/* Room for the status icon, so a long address never runs under it. */
.email-field-kiosk input.form-control {
    padding-right: 44px;
}

.email-field-status {
    position: absolute;
    top: 50%;
    right: 14px;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    /* The icon is a report, not a target: taps belong to the field under it. */
    pointer-events: none;
    font-size: 18px;
}

.email-field-spinner {
    color: #6c7ae0;
}

.email-field-check {
    color: #2fb344;
}

.email-field-warn {
    color: #dc3545;
}
</style>